import * as vp from 'vopay';
import * as Yup from 'yup';
const { TextField, Select } = vp;
import { FieldData } from '../../../../Form/types';
import { useTranslation } from 'react-i18next';

type AccountTranferRowsProp = {
  MainAccountName: string;
  MainAccountId: string;
  MainAccountBalance: string;
  subAccounts: Record<string, vp.SubAccountsLists>;
  country: string;
};

export type AccountTransferRequest = {
  Amount: string;
  RecipientAccountID: string;
  DebitorAccountID: string;
  Currency: string;
};
export type AccountTransferSerializedRequest = {
  Amount: number;
  RecipientAccountID: string;
  DebitorAccountID: string;
  Currency: string;
};

export const buildAccountTransferRows = ({
  MainAccountName,
  MainAccountBalance,
  MainAccountId,
  subAccounts,
  country,
}: AccountTranferRowsProp): FieldData<AccountTransferRequest>[] => {
  const { t } = useTranslation('translations', { keyPrefix: 'SubAccounts.List.OrgChart.AccountTransfer' });

  const CurrencyOptions = {
    ...vp.CURRENCY_LIST_OPTIONS,
  };

  return [
    {
      label: () => t('Currency.label'),
      fieldName: 'Currency',
      getInputState: () => 'required',
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return (
          <Select
            version="2"
            options={country === 'US' ? vp.convertToOptions(vp.US_CURRENCY_ONLY) : vp.convertToOptions(CurrencyOptions)}
            placeholder={t('Currency.placeholder')}
            {...fieldData}
          />
        );
      },
      showField: () => true,
      validate: () => Yup.string().required(),
    },
    {
      label: () => t('From.label'),
      fieldName: 'DebitorAccountID',
      getInputState: () => 'required',
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({});
        const FromBankAccountsOptions = buildSubAccountOptions(
          Object.values(subAccounts).filter((data) => data.AccountStatus === 'Active'),
          ctx.values.Currency,
          false,
        );
        return (
          <Select
            version="2"
            options={FromBankAccountsOptions}
            placeholder={t('From.placeholder')}
            sort={false}
            filterable
            {...fieldData}
          />
        );
      },
      showField: () => true,
      validate: () => Yup.string().required(),
    },
    {
      label: () => t('To.label'),
      fieldName: 'RecipientAccountID',
      getInputState: () => 'required',
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({});
        const ToBankAccountsOptions = buildSubAccountOptions(
          Object.values(subAccounts).filter(
            (data) => ctx.values.DebitorAccountID !== data.AccountID && data.AccountStatus === 'Active',
          ),
          ctx.values.Currency,
          ctx.values.DebitorAccountID === MainAccountId,
        );
        return (
          <Select
            version="2"
            options={ToBankAccountsOptions}
            placeholder={t('To.placeholder')}
            sort={false}
            filterable
            {...fieldData}
          />
        );
      },
      showField: () => true,
      validate: () => Yup.string().required(),
    },
    {
      label: () => t('Amount.label'),
      fieldName: 'Amount',
      getInputState: () => 'required',
      serializeField: (value) => Number(vp.sanitizeToNumeric(value.Amount)),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return <TextField preset="currency" {...fieldData} variant="outlineShadow" />;
      },
      validate: (ctx) => {
        const availableBalance =
          ctx.values.DebitorAccountID == MainAccountId
            ? MainAccountBalance
            : Object.values(
                Object.values(subAccounts).find((data) => data.AccountID === ctx.values.DebitorAccountID)?.Balances ||
                  {},
              ).find((balance) => balance.Currency === ctx.values.Currency)?.AvailableBalance;

        return Yup.string()
          .test('amount-validation', t('Validation.minimumAmount'), (value) => {
            const amountValidation = Number(vp.sanitizeToNumeric(value)) <= 0.0;
            return !amountValidation;
          })
          .test(
            'amount-validation',
            t('Validation.insufficientFunds', { maxAmount: vp.formattedCurrency(String(availableBalance)) }),
            (value) => {
              const availableFundsValidation =
                Number(vp.sanitizeToNumeric(value)) > Number(vp.sanitizeToNumeric(availableBalance));
              return !availableFundsValidation;
            },
          )
          .required();
      },
      showField: () => true,
    },
  ];
  function buildSubAccountOptions(
    lists: vp.SubAccountsLists[],
    currencySelected: string,
    removeMain: boolean,
  ): { label: string; value: string }[] {
    const initialOption = removeMain
      ? []
      : [
          {
            label: `${MainAccountName} ($${vp.formattedCurrency(MainAccountBalance || '0.00')})`,
            value: MainAccountId,
          },
        ];

    return [
      ...initialOption,
      ...lists
        .sort((a, b) => a.LegalBusinessName.localeCompare(b.LegalBusinessName))
        .map((data) => ({
          value: data.AccountID,
          label: `${data.LegalBusinessName} ($${vp.formattedCurrency(
            Object.values(data.Balances).find((balance) => balance.Currency === currencySelected)?.AvailableBalance ||
              '0.00',
          )})`,
        })),
    ];
  }
};
